import { useState, useEffect, useRef } from "react";
import { useApp, api } from "./context";

const REACTIONS = ["❤️", "😍", "🔥", "😮", "😂", "👏"];

const ringGrad = (plan, allViewed) => {
  if (allViewed) return "linear-gradient(135deg,#888,#555)";
  if (plan === "elite") return "linear-gradient(135deg,#f59e0b,#ef4444,#e91e8c)";
  if (plan === "premium") return "linear-gradient(135deg,#6c5ce7,#a855f7)";
  return "linear-gradient(135deg,#10b981,#6c5ce7)";
};

const timeLeft = (exp) => {
  const diff = new Date(exp) - Date.now();
  if (diff <= 0) return "Expired";
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return h > 0 ? `${h}h left` : `${m}m left`;
};

// ── STORY VIEWER ──
const StoryViewer = ({ groups, startGroupIndex, onClose, onDelete, currentUserId, onReact, onReply }) => {
  const [groupIdx, setGroupIdx] = useState(startGroupIndex);
  const [storyIdx, setStoryIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [showReactions, setShowReactions] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [localReactions, setLocalReactions] = useState({});
  const [localReplies, setLocalReplies] = useState({});
  const [sending, setSending] = useState(false);
  const [flyingEmojis, setFlyingEmojis] = useState([]);
  const timerRef = useRef(null);
  const DURATION = 5000;

  const group = groups[groupIdx];
  const story = group?.stories[storyIdx];

  const goNext = () => {
    clearInterval(timerRef.current);
    setProgress(0);
    setShowReactions(false);
    if (storyIdx < group.stories.length - 1) {
      setStoryIdx(i => i + 1);
    } else if (groupIdx < groups.length - 1) {
      setGroupIdx(i => i + 1);
      setStoryIdx(0);
    } else {
      onClose();
    }
  };

  const goPrev = () => {
    clearInterval(timerRef.current);
    setProgress(0);
    setShowReactions(false);
    if (storyIdx > 0) {
      setStoryIdx(i => i - 1);
    } else if (groupIdx > 0) {
      setGroupIdx(i => i - 1);
      setStoryIdx(0);
    }
  };

  useEffect(() => {
    if (!story) return;
    api("/stories/" + story.id + "/view", { method: "POST" }).catch(() => {});
    setProgress(0);
    setShowReactions(false);
    setShowReplies(false);
    setReplyText("");
    clearInterval(timerRef.current);
    if (story.media_type === "image") {
      let elapsed = 0;
      const tick = 100;
      timerRef.current = setInterval(() => {
        // Pause timer when reply box has text or replies panel is open
        if (document.activeElement?.placeholder === "Reply to story...") return;
        elapsed += tick;
        setProgress(Math.min((elapsed / DURATION) * 100, 100));
        if (elapsed >= DURATION) goNext();
      }, tick);
    }
    return () => clearInterval(timerRef.current);
  }, [groupIdx, storyIdx]); // eslint-disable-line

  const handleReact = async (emoji) => {
    setShowReactions(false);
    const storyId = story.id;
    const prev = localReactions[storyId] ?? story.my_reaction;
    const newEmoji = prev === emoji ? null : emoji;
    setLocalReactions(r => ({ ...r, [storyId]: newEmoji }));
    // Launch flying emoji
    if (newEmoji) {
      const id = Date.now();
      const x = 30 + Math.random() * 40;
      setFlyingEmojis(f => [...f, { id, emoji, x }]);
      setTimeout(() => setFlyingEmojis(f => f.filter(e => e.id !== id)), 1800);
    }
    await api(`/stories/${storyId}/react`, {
      method: "POST",
      body: JSON.stringify({ emoji }),
    });
  };

  const handleReply = async () => {
    if (!replyText.trim() || sending) return;
    setSending(true);
    const content = replyText.trim();
    setReplyText("");
    // Send silently to story owner DM — stay on story
    const storyThumb = story.thumbnail_url || (story.media_type === "image" ? story.media_url : null);
    const dmContent = storyThumb
      ? `💬 Replied to your story: ${content}`
      : `💬 Replied to your story: ${content}`;
    await api(`/conversations/start`, {
      method: "POST",
      body: JSON.stringify({ user_id: group.user_id, content: dmContent }),
    });
    setSending(false);
    // Show inline confirmation — don't close story
    const storyId = story.id;
    const tempReply = {
      id: Date.now(),
      content: content,
      user_name: "You",
      user_emoji: "👤",
      avatar_url: null,
      created_at: new Date().toISOString(),
    };
    setLocalReplies(r => ({ ...r, [storyId]: [...(r[storyId] || story.replies || []), tempReply] }));
    if (onReply) onReply();
  };

  if (!group || !story) return null;
  const isMine = group.user_id === currentUserId;
  const myReaction = localReactions[story.id] !== undefined ? localReactions[story.id] : story.my_reaction;
  const replies = localReplies[story.id] || story.replies || [];
  const reactions = story.reactions || [];

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{`
        @keyframes flyEmoji {
          0% { transform: translateY(0) scale(1); opacity: 1; }
          100% { transform: translateY(-280px) scale(1.6); opacity: 0; }
        }
      `}</style>
      <div style={{ width: "100%", maxWidth: 420, height: "100%", maxHeight: "100dvh", position: "relative", background: "#111", display: "flex", flexDirection: "column" }}>

        {/* Flying emojis */}
        {flyingEmojis.map(fe => (
          <div key={fe.id} style={{ position: "absolute", bottom: 80, left: `${fe.x}%`, fontSize: 36, zIndex: 50, pointerEvents: "none", animation: "flyEmoji 1.8s ease-out forwards" }}>
            {fe.emoji}
          </div>
        ))}

        {/* Progress bars */}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 10, display: "flex", gap: 3, padding: "10px 10px 0" }}>
          {group.stories.map((_, i) => (
            <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: "rgba(255,255,255,0.3)", overflow: "hidden" }}>
              <div style={{ height: "100%", borderRadius: 2, background: "#fff", width: i < storyIdx ? "100%" : i === storyIdx ? progress + "%" : "0%", transition: i === storyIdx ? "none" : undefined }} />
            </div>
          ))}
        </div>

        {/* Header */}
        <div style={{ position: "absolute", top: 20, left: 0, right: 0, zIndex: 10, display: "flex", alignItems: "center", gap: 10, padding: "0 14px" }}>
          <div style={{ width: 38, height: 38, borderRadius: 19, padding: 2, background: ringGrad(group.user_plan, false) }}>
            <div style={{ width: "100%", height: "100%", borderRadius: 17, background: group.avatar_url ? `url(${group.avatar_url}) center/cover` : "#222", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, overflow: "hidden" }}>
              {!group.avatar_url && (group.user_emoji || "👤")}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{group.user_name}</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)" }}>{timeLeft(story.expires_at)}</div>
          </div>
          {isMine && (
            <button onClick={() => onDelete(story.id)} style={{ background: "rgba(239,68,68,0.2)", border: "1px solid #ef4444", color: "#ef4444", borderRadius: 8, padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Delete</button>
          )}
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", width: 30, height: 30, borderRadius: 15, fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        </div>

        {/* Media */}
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
          {story.media_type === "video" ? (
            <video key={story.id} src={story.media_url} autoPlay playsInline style={{ width: "100%", height: "100%", objectFit: "contain" }} onEnded={goNext} />
          ) : (
            <img key={story.id} src={story.media_url} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          )}

          {/* Caption */}
          {story.caption && (
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "40px 16px 10px", background: "linear-gradient(transparent,rgba(0,0,0,0.75))" }}>
              <div style={{ fontSize: 14, color: "#fff", fontWeight: 500, lineHeight: 1.5 }}>{story.caption}</div>
            </div>
          )}

          {/* Tap zones */}
          <div onClick={goPrev} style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "35%", zIndex: 5 }} />
          <div onClick={goNext} style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "35%", zIndex: 5 }} />
        </div>

        {/* Reactions summary */}
        {reactions.length > 0 && (
          <div style={{ position: "absolute", bottom: 110, left: 16, display: "flex", gap: 4, zIndex: 10 }}>
            {reactions.map((r, i) => (
              <span key={i} style={{ fontSize: 13, background: "rgba(0,0,0,0.5)", borderRadius: 20, padding: "3px 8px" }}>
                {r.emoji} {r.count}
              </span>
            ))}
          </div>
        )}

        {/* View count for owner */}
        {isMine && (
          <div style={{ position: "absolute", bottom: 110, right: 16, zIndex: 10 }}>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", fontWeight: 600, background: "rgba(0,0,0,0.4)", padding: "3px 8px", borderRadius: 10 }}>👁 {story.view_count}</span>
          </div>
        )}

        {/* Reaction picker popup */}
        {showReactions && (
          <div style={{ position: "absolute", bottom: 90, left: "50%", transform: "translateX(-50%)", zIndex: 20, background: "rgba(30,30,40,0.95)", borderRadius: 40, padding: "10px 14px", display: "flex", gap: 10, border: "1px solid rgba(255,255,255,0.1)", boxShadow: "0 8px 32px rgba(0,0,0,0.6)" }}>
            {REACTIONS.map(em => (
              <button key={em} onClick={() => handleReact(em)} style={{ fontSize: 26, background: myReaction === em ? "rgba(255,255,255,0.2)" : "transparent", border: "none", cursor: "pointer", borderRadius: 20, padding: "4px 6px", transform: myReaction === em ? "scale(1.3)" : "scale(1)", transition: "transform 0.15s" }}>
                {em}
              </button>
            ))}
          </div>
        )}

        {/* Bottom bar — reactions + replies */}
        {!isMine && (
          <div style={{ background: "rgba(0,0,0,0.6)", padding: "8px 12px", zIndex: 10, display: "flex", alignItems: "center", gap: 8 }}>
            {/* Reply input */}
            <input
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleReply()}
              placeholder="Reply to story..."
              style={{ flex: 1, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 20, padding: "8px 14px", color: "#fff", fontSize: 13, outline: "none", fontFamily: "inherit" }}
            />
            {replyText.trim() ? (
              <button onClick={handleReply} disabled={sending} style={{ width: 36, height: 36, borderRadius: 18, border: "none", background: "linear-gradient(135deg,#6c5ce7,#e91e8c)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M7 17L18 12L7 7L9 11L14 12L9 13L7 17Z" fill="#fff"/></svg>
              </button>
            ) : (
              <button onClick={() => setShowReactions(r => !r)} style={{ width: 36, height: 36, borderRadius: 18, border: "1px solid rgba(255,255,255,0.2)", background: myReaction ? "rgba(255,255,255,0.15)" : "transparent", cursor: "pointer", fontSize: 20, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {myReaction || "❤️"}
              </button>
            )}
            {/* Replies toggle */}
            {replies.length > 0 && (
              <button onClick={() => setShowReplies(r => !r)} style={{ background: "rgba(255,255,255,0.12)", border: "none", color: "#fff", borderRadius: 16, padding: "6px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
                💬 {replies.length}
              </button>
            )}
          </div>
        )}

        {/* Replies panel */}
        {showReplies && replies.length > 0 && (
          <div style={{ background: "rgba(10,10,20,0.95)", maxHeight: 200, overflowY: "auto", padding: "10px 14px", zIndex: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.5)", marginBottom: 8 }}>REPLIES</div>
            {replies.map((r, i) => (
              <div key={r.id || i} style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "flex-start" }}>
                <div style={{ width: 28, height: 28, borderRadius: 14, background: r.avatar_url ? `url(${r.avatar_url}) center/cover` : "linear-gradient(135deg,#6c5ce7,#e91e8c)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, flexShrink: 0, overflow: "hidden" }}>
                  {!r.avatar_url && (r.user_emoji || r.user_name?.[0] || "?")}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#fff", marginBottom: 2 }}>{r.user_name}</div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.8)", lineHeight: 1.4 }}>{r.content}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── STORIES BAR ──
const StoriesBar = () => {
  const { user, dark, c, flash } = useApp();
  const [groups, setGroups] = useState([]);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerGroupIdx, setViewerGroupIdx] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [showCaption, setShowCaption] = useState(false);
  const [caption, setCaption] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingPreview, setPendingPreview] = useState(null);
  const [showAddOptions, setShowAddOptions] = useState(false);
  // reading state removed — no longer needed
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);

  const loadStories = () => {
    api("/stories").then(r => { if (r.ok) setGroups(r.data?.data || []); });
  };

  useEffect(() => { loadStories(); }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    const isImage = file.type.startsWith("image/");
    if (!isVideo && !isImage) { flash("Only images or videos", "error"); return; }
    if (isVideo && file.size > 100 * 1024 * 1024) { flash("Video max 100MB", "error"); return; }
    if (isImage && file.size > 20 * 1024 * 1024) { flash("Image max 20MB", "error"); return; }
    // Create object URL immediately — synchronous, no delay
    const objectUrl = URL.createObjectURL(file);
    // Store file reference BEFORE clearing input
    const fileRef = file;
    // Clear input so same file can be selected again later
    setTimeout(() => { try { e.target.value = ""; } catch {} }, 100);
    setShowAddOptions(false);
    setCaption("");
    setPendingFile(fileRef);
    setPendingPreview({ url: objectUrl, type: fileRef.type });
    setShowCaption(true);
  };

  const uploadStory = async () => {
    if (!pendingFile) return;
    setUploading(true);
    setShowCaption(false);
    const form = new FormData();
    form.append("media", pendingFile);
    if (caption.trim()) form.append("caption", caption.trim());
    const token = localStorage.getItem("gc_token");
    const apiBase = process.env.REACT_APP_API_URL || "https://community.geilstore.com/api";
    // Revoke object URL to free memory
    if (pendingPreview?.url?.startsWith("blob:")) {
      URL.revokeObjectURL(pendingPreview.url);
    }
    try {
      const res = await fetch(apiBase + "/stories", {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
        body: form,
      });
      if (res.ok) { flash("Story posted! 🎉"); loadStories(); }
      else { flash("Upload failed, try again", "error"); }
    } catch {
      flash("Upload failed, check connection", "error");
    }
    setUploading(false);
    setPendingFile(null);
    setPendingPreview(null);
    setCaption("");
  };

  const handleDelete = async (storyId) => {
    const res = await api("/stories/" + storyId, { method: "DELETE" });
    if (res.ok) { flash("Story deleted"); loadStories(); setViewerOpen(false); }
    else flash("Delete failed", "error");
  };

  const openViewer = (idx) => { setViewerGroupIdx(idx); setViewerOpen(true); };

  const myGroup = groups.find(g => g.is_mine);
  const othersGroups = groups.filter(g => !g.is_mine);

  // ── Add Story Options Sheet ──
  const AddStorySheet = () => (
    <div onClick={() => setShowAddOptions(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9990, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 480, background: dark ? "#1a1b2e" : "#fff", borderRadius: "20px 20px 0 0", padding: "16px 20px 40px", boxShadow: "0 -8px 40px rgba(0,0,0,0.4)" }}>
        <div style={{ width: 36, height: 3, borderRadius: 2, background: "rgba(128,128,128,0.3)", margin: "0 auto 20px" }} />
        <div style={{ fontSize: 15, fontWeight: 800, color: dark ? "#f0f0fa" : "#1a1c2e", marginBottom: 16 }}>Add to Story</div>
        <div style={{ display: "flex", gap: 12 }}>
          {/* Camera */}
          <label style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "16px 8px", borderRadius: 16, border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "#e5e7eb"}`, background: dark ? "rgba(255,255,255,0.04)" : "#f8f8ff", cursor: "pointer" }}>
            <input ref={cameraRef} type="file" accept="image/*,video/*" capture="environment" style={{ display: "none" }} onChange={handleFileSelect} />
            <div style={{ width: 48, height: 48, borderRadius: 24, background: "linear-gradient(135deg,#6c5ce7,#e91e8c)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: dark ? "#f0f0fa" : "#1a1c2e" }}>Camera</span>
          </label>
          {/* Gallery */}
          <label style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "16px 8px", borderRadius: 16, border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "#e5e7eb"}`, background: dark ? "rgba(255,255,255,0.04)" : "#f8f8ff", cursor: "pointer" }}>
            <input ref={galleryRef} type="file" accept="image/*,video/*" style={{ display: "none" }} onChange={handleFileSelect} />
            <div style={{ width: 48, height: 48, borderRadius: 24, background: "linear-gradient(135deg,#10b981,#6c5ce7)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: dark ? "#f0f0fa" : "#1a1c2e" }}>Gallery</span>
          </label>
          {/* Video */}
          <label style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "16px 8px", borderRadius: 16, border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "#e5e7eb"}`, background: dark ? "rgba(255,255,255,0.04)" : "#f8f8ff", cursor: "pointer" }}>
            <input type="file" accept="video/*" capture="environment" style={{ display: "none" }} onChange={handleFileSelect} />
            <div style={{ width: 48, height: 48, borderRadius: 24, background: "linear-gradient(135deg,#ef4444,#f59e0b)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: dark ? "#f0f0fa" : "#1a1c2e" }}>Video</span>
          </label>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Caption/Preview — fullscreen like Instagram */}
      {showCaption && (
        <div style={{ position: "fixed", inset: 0, background: "#000", zIndex: 9998, display: "flex", flexDirection: "column" }}>
          {/* Media — full screen */}
          <div style={{ flex: 1, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {pendingPreview && (
              pendingPreview.type?.startsWith("video") ?
                <video src={pendingPreview.url} controls playsInline style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} /> :
                <img src={pendingPreview.url} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
            )}
            {/* Top bar */}
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, padding: "16px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "linear-gradient(rgba(0,0,0,0.5),transparent)" }}>
              <button onClick={() => { setShowCaption(false); setPendingFile(null); setPendingPreview(null); }} style={{ width: 36, height: 36, borderRadius: 18, background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.2)", color: "#fff", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
              <span style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>New Story</span>
              <div style={{ width: 36 }} />
            </div>
          </div>
          {/* Bottom — caption + post */}
          <div style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(12px)", padding: "14px 16px", paddingBottom: "max(14px,env(safe-area-inset-bottom))" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <input value={caption} onChange={e => setCaption(e.target.value.slice(0, 200))} placeholder="Add a caption..." style={{ flex: 1, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 22, padding: "10px 16px", color: "#fff", fontSize: 14, outline: "none", fontFamily: "inherit" }} />
              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", flexShrink: 0 }}>{caption.length}/200</span>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => { setShowCaption(false); setPendingFile(null); setPendingPreview(null); }} style={{ flex: 1, padding: "13px", borderRadius: 14, border: "1px solid rgba(255,255,255,0.2)", background: "transparent", color: "rgba(255,255,255,0.7)", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button onClick={uploadStory} disabled={uploading} style={{ flex: 2, padding: "13px", borderRadius: 14, border: "none", background: uploading ? "rgba(108,92,231,0.5)" : "linear-gradient(135deg,#6c5ce7,#e91e8c)", color: "#fff", fontSize: 14, fontWeight: 800, cursor: uploading ? "default" : "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                {uploading ? <><div style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: 8, animation: "spin 0.8s linear infinite" }} />Posting...</> : "Post Story 🚀"}
              </button>
            </div>
          </div>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      )}

      {/* Add story options */}
      {showAddOptions && <AddStorySheet />}

      {/* Story Viewer */}
      {viewerOpen && groups.length > 0 && (
        <StoryViewer
          groups={groups}
          startGroupIndex={viewerGroupIdx}
          onClose={() => { setViewerOpen(false); loadStories(); }}
          onDelete={handleDelete}
          currentUserId={user?.id}
          onReact={() => {}}
          onReply={() => {}}
        />
      )}

      {/* Stories bar */}
      <div style={{ padding: "12px 0 8px", marginBottom: 4 }}>
        <div style={{ display: "flex", gap: 12, overflowX: "auto", overflowY: "visible", scrollbarWidth: "none", padding: "4px 2px 8px" }}>

          {/* Add Story / My Story */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 }}>
            <div style={{ position: "relative" }}>
              {/* Main circle — thumbnail from latest story or profile pic */}
              <div
                onClick={() => myGroup ? openViewer(groups.findIndex(g => g.is_mine)) : setShowAddOptions(true)}
                style={{ width: 54, height: 54, borderRadius: 27, padding: myGroup ? 2.5 : 0, background: myGroup ? ringGrad("free", myGroup.all_viewed) : "transparent", cursor: "pointer" }}
              >
                <div style={{ width: myGroup ? "100%" : 54, height: myGroup ? "100%" : 54, borderRadius: 24, overflow: "hidden", border: myGroup ? `2px solid ${dark ? "#0b0c10" : "#fff"}` : `2px dashed ${dark ? "#ffffff40" : "#ccc"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, background: dark ? "#ffffff10" : "#f0f0f0" }}>
                  {myGroup ? (
                    // Show thumbnail of latest image story, fallback to profile pic
                    (() => {
                      const latest = myGroup.stories.slice().reverse().find(s => s.thumbnail_url || s.media_type === "image");
                      const thumbSrc = latest?.thumbnail_url || latest?.media_url;
                      if (thumbSrc) return <img src={thumbSrc} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />;
                      if (user?.avatarUrl) return <img src={user.avatarUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />;
                      return <span>{user?.emoji || "👤"}</span>;
                    })()
                  ) : (
                    user?.avatarUrl
                      ? <img src={user.avatarUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <span>{user?.emoji || "👤"}</span>
                  )}
                </div>
              </div>

              {/* + button — always visible when has stories or not */}
              <div
                onClick={() => setShowAddOptions(true)}
                style={{ position: "absolute", bottom: 0, right: 0, width: 20, height: 20, borderRadius: 10, background: myGroup ? "linear-gradient(135deg,#6c5ce7,#e91e8c)" : "#6c5ce7", border: `2px solid ${dark ? "#0b0c10" : "#fff"}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", zIndex: 2 }}
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </div>

              {uploading && (
                <div style={{ position: "absolute", inset: 0, borderRadius: 27, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ width: 18, height: 18, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: 9, animation: "spin 0.8s linear infinite" }} />
                </div>
              )}
            </div>
            <span style={{ fontSize: 10, color: c.faint, fontWeight: 600 }}>{myGroup ? "My Story" : uploading ? "Posting..." : "Add Story"}</span>
          </div>

          {/* Others' stories */}
          {othersGroups.map((g) => (
            <div key={g.user_id} onClick={() => openViewer(groups.findIndex(gr => gr.user_id === g.user_id))} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0, cursor: "pointer" }}>
              <div style={{ width: 54, height: 54, borderRadius: 27, padding: 2.5, background: ringGrad(g.user_plan, g.all_viewed) }}>
                <div style={{ width: "100%", height: "100%", borderRadius: 24, border: `2px solid ${dark ? "#0b0c10" : "#fff"}`, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, background: dark ? "#1a1a2e" : "#f5f5f5" }}>
                  {(() => {
                    const latest = g.stories?.slice().reverse().find(s => s.thumbnail_url || s.media_type === "image");
                    const thumbSrc = latest?.thumbnail_url || latest?.media_url;
                    if (thumbSrc) return <img src={thumbSrc} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />;
                    if (g.avatar_url) return <img src={g.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />;
                    return <span>{g.user_emoji || "👤"}</span>;
                  })()}
                </div>
              </div>
              <span style={{ fontSize: 10, color: g.all_viewed ? c.faint : c.text, fontWeight: g.all_viewed ? 500 : 700, maxWidth: 54, textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {g.user_name ? g.user_name.split(" ")[0] : "User"}
              </span>
            </div>
          ))}

          {groups.length === 0 && !uploading && (
            <div style={{ display: "flex", alignItems: "center", padding: "8px 4px", opacity: 0.5 }}>
              <span style={{ fontSize: 12, color: c.faint }}>No stories yet — be the first!</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default StoriesBar;